import { InclusionScoreAnalysisCardProps } from '@diversioteam/diversio-ds'
import { loadPastSurveyInformation } from 'sagas/network'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { DataItem } from '@diversioteam/diversio-ds/dist/components/core/InclusionScoreAnalysisCard/inclusionScoreAnalysisCard.types'

import { Queries } from 'api/queries.enum'

import { useQuery } from './useQuery'

type InclusionScoreAnalysisProps = {
    inclusionScores: InclusionScoreAnalysisCardProps['data']
    industryKpis: InclusionScoreAnalysisCardProps['benchmarkOptions']
}

type PastSurveyInformationProps = {
    nextRecommendedSurvey: string
    isNextRecommendedSurveyOverdue: boolean
    pastSurveys: PastSurvey[]
}

type PastSurvey = {
    yearQuarterLabel: string
    dateCreated: string
    numberOfSurveys: number
    participationRate: number
    numberOfResponses: number
    inclusionScore: InclusionScoreProps
}

type InclusionScoreProps = {
    score: string
    industries: IndustryProps[]
}

type IndustryProps = {
    label: string
    uuid: string
    isActive: boolean
    isPrimary: boolean
    score: string
}

type IndustryKpiProps = {
    label: string
    isPrimary?: boolean
    score: string
}

export const useInclusionScoreAnalysis = () => {
    const { t } = useTranslation()

    const transformData = (rawData: PastSurveyInformationProps): InclusionScoreAnalysisProps => {
        const parseSurvey = (survey: PastSurvey): DataItem => {
            return {
                yearQuarterLabel: survey.yearQuarterLabel,
                score: survey.inclusionScore.score,
                industries: survey.inclusionScore.industries.map(parseIndustry),
            }
        }

        const parseIndustry = (survey: IndustryProps): IndustryKpiProps => {
            return {
                label: survey.label,
                isPrimary: survey.isPrimary,
                score: survey.score,
            }
        }

        const data = {
            inclusionScores: [...rawData.pastSurveys].reverse().map(parseSurvey),
            industryKpis: rawData.pastSurveys[0].inclusionScore.industries.map(parseIndustry),
        }

        return data
    }

    return useQuery<any, Error>({
        queryKey: [Queries.getPastSurveyInformation],
        queryFn: loadPastSurveyInformation,
        select: useCallback(transformData, [t]),
    })
}
